import { needsProfile } from "../components/routes/ProfileRequiredGate";

// #1449 R22 (revisor final, vuelta 1): "Mis lugares de trabajo" con 0
// unidades en NINGÚN lugar de la cuenta mostraba el panel completo de
// `LugaresConfig` vacío ("Sin resultados para ''", "Elige un lugar o una
// unidad de la izquierda, o crea uno nuevo") sin ninguna salida: el mismo
// diálogo sin salida que R2 ya había arreglado para el paso "Configura tu
// unidad" del checklist (`onConfigurarUnidad`), pero que quedó SIN arreglar
// para el menú ("Mis lugares de trabajo…"), el selector de unidad
// ("Configurar unidades") y cualquier otro camino que abriera `LugaresConfig`
// directo.
//
// Sin React ni estado propio a propósito (lo único que importa es
// `needsProfile`, que lee la marca de "Después" del localStorage): así se puede
// probar sin montar Turno.js entero (1449-front, ronda "arreglos revisor final").
export const debeAbrirFormularioCorto = (hayUnidades) => !hayUnidades;

// #1453 R12 / #1534: hay un gate de alta VISIBLE cuando algún overlay tapa el
// Turno. Es la lista que miran el hook `useHuboGateDeAlta` y el gate de unidad
// de Turno.js (`Turno.gateUnidad.test.js` lo comprueba sobre el AST).
// Cada término es lo que usa el componente del gate para dibujarse:
//   Términos          → `tos_needs_acceptance` (TosAcceptanceGate)
//   celular y perfil  → `needsProfile` (ProfileRequiredGate): el celular nunca
//                       se salta; etapa e institución se apagan con "Después"
//   prueba vencida, pago vencido y sin acceso →
//                       `access.permite === false` (SuscripcionGate; son los
//                       códigos con que billing/access.py:access_state corta
//                       el acceso; los documentos ya no cortan, #1529).
//                       Incluye `inactividad` (pausa por inactividad, #1583,
//                       R26): PausaInactividadGate la dibuja con
//                       `access.permite === false`, así que la intro, el
//                       ReintroGate y el formulario de primera unidad esperan
//                       solos, sin cambio de lógica acá.
// Lo que no dibuja un overlay no cuenta, aunque se le parezca. `trial_expired`
// suelto llega `true` con `access.permite: true` cuando la demo venció pero la
// cuenta tiene acceso igual: exentas (superuser, plan interno o técnica) y
// suscripciones que vienen de una demo (user_api/serializers.py:get_trial_expired
// solo mira `trial_ends_at`). `phone_required` sin `missing_profile_fields`
// (backend viejo) no pinta nada. Si contaran, el gate de unidad esperaría un
// overlay que no existe y el médico quedaría sin selector. Mirar
// `missing_profile_fields` crudo tenía el mismo defecto con "Después". Tests:
// "#1534 · hayGateDeAltaActivo cuenta solo lo que de verdad se dibuja".
// TRAMPA ABIERTA (#1534): un overlay que se prende por un 403 en vivo (no viene en `currentUser`) o que aparece con un diálogo ya abierto (selector, intro, censo) queda encima de un Dialog que conserva su focus trap.
export const hayGateDeAltaActivo = (user) =>
  Boolean(
    user?.tos_needs_acceptance ||
      needsProfile(user) ||
      user?.access?.permite === false,
  );

// #1453 R12 ("nunca dos popups seguidos en la misma entrada"): el formulario
// corto de primera unidad se abre SOLO si no hay gate ahora Y no lo hubo antes
// en esta entrada. Mirar solo el gate de ahora no alcanza: el effect de
// Turno.js decide cuando terminan de cargar las pestañas y los lugares (puede
// tardar ~12 s), y si el médico guardó el celular antes, `refreshUser` ya
// limpió `phone_required` y el formulario salía justo detrás del overlay.
export const puedeAbrirPrimeraUnidadSola = ({
  lugaresOk,
  gateActivoAhora,
  huboGateEnEstaEntrada,
  tutorialOpen,
  yaDescartada,
}) =>
  Boolean(lugaresOk) &&
  !gateActivoAhora &&
  !huboGateEnEstaEntrada &&
  !tutorialOpen &&
  !yaDescartada;

// #1520 ronda 3 (R11) y #1534: UNA sola decisión de "qué diálogo de unidad abre
// el gate de arranque". La aplica `despachar`, en el effect del gate (Turno.js).
// El CTA "Actualizar mis camas" de la intro no la llama. Si el gate todavía no
// corrió, deja el censo pendiente y el gate lo abre (después del selector o del
// formulario, o en su lugar); si ya corrió (intro reabierta desde el menú),
// abre él mismo el formulario corto o el censo
// (`Turno.js:handleIntroActualizarCamas`). Nunca hay dos diálogos. Devuelve:
//   "esperar"    → hay un gate de alta visible (o el tutorial abierto): todavía
//                  no se decide nada, ni diálogo ni censo pendiente, y el effect
//                  NO marca "manejado", así vuelve a evaluar cuando se cierre
//   "selector"   → WorkplacePickerDialog ("¿Dónde trabajas hoy?")
//   "formulario" → PrimeraUnidadDialog (0 unidades confirmadas, #1449 R1)
//   null         → el gate no abre ningún diálogo
// `cacheHoy`: lo que hay en el cache local del día (null, "__skip__" o un id).
// `sesionDeHoy`: la sesión de hoy del servidor ({ unidadId } o null).
// #1534: antes el camino del selector no miraba ningún gate. El Dialog de MUI
// (z-index 1300) quedaba montado debajo de ProfileRequiredGate (20000) y su
// focus trap le robaba el teclado al formulario del perfil. El selector no mira
// `huboGateEnEstaEntrada`: es el paso del día y abre apenas el gate se cierra.
// El formulario es una oferta que puede esperar a la próxima entrada (#1453
// R12, nunca dos popups seguidos).
export const decidirDialogoUnidad = ({
  hayUnidades,
  sesionDeHoy,
  cacheHoy,
  otroGateActivo,
  huboGateEnEstaEntrada,
  tutorialOpen,
  primeraUnidadDescartada,
  lugaresOk,
}) => {
  // Hay sesión de hoy con unidad, o ya hay una decisión cacheada: nada que preguntar.
  if (sesionDeHoy?.unidadId) return null;
  if (cacheHoy != null) return null;
  if (otroGateActivo || tutorialOpen) return "esperar";
  if (hayUnidades) return "selector";
  // #1453 R12: la misma regla de `puedeAbrirPrimeraUnidadSola` (incluye "hubo
  // un gate de alta antes en esta entrada"), para no apilar dos popups.
  if (
    puedeAbrirPrimeraUnidadSola({
      lugaresOk,
      gateActivoAhora: otroGateActivo,
      huboGateEnEstaEntrada,
      tutorialOpen,
      yaDescartada: primeraUnidadDescartada,
    })
  ) {
    return "formulario";
  }
  return null;
};

// #1520 F14 / R11: cuando el CTA "Actualizar mis camas" no tiene ningún diálogo
// de unidad que abrir antes, el censo se abre sobre la pestaña del TURNO VIGENTE.
// Antes se abría sobre `selectedTab`, que con 0 unidades y solo la pestaña de
// ayer era la de AYER (formulario corto bloqueado por un gate de alta o por
// "Ahora no"). De día el turno vigente es el de hoy; de madrugada (00:00 a
// 07:59) con la sesión de ayer en curso es el de AYER (#820, ver
// `tituloTurnoVigente` en turno-en-curso.js), igual que el botón "Camas" de la
// base: ahí NO se crea un día nuevo a mitad del turno de noche (#1520 F18).
// `todayTitle` es ese título vigente, no el `getTodayDateString()` crudo.
// Devuelve:
//   "abrir"   → la pestaña mirada ya es la vigente: abrir el censo ahí
//   "activar" → hay pestaña vigente pero se mira otra: activarla y abrir
//   "crear"   → no hay pestaña vigente: crearla (confirmCreateTab) y abrir
// `hoyTab`: la pestaña vigente de `tabsDetail` (o null).
export const decidirCensoSobreHoy = ({ selectedTabTitle, todayTitle, hoyTab }) => {
  if (selectedTabTitle && selectedTabTitle === todayTitle) return "abrir";
  return hoyTab ? "activar" : "crear";
};
